/**
 * Provably-fair core (v2) — TypeScript mirror of the reference implementation.
 *
 * Spec: marbles-stack-go/docs/provably-fair-standard.md. This MUST stay
 * byte-identical to 155-game-crash/fairness/core.py; both are locked by the
 * shared fairness-vectors.json (see fairnessVectors.test.ts). Do NOT add
 * game-specific logic here — result mapping lives in mappers.ts.
 *
 * WebCrypto (async) so it runs in the browser verifier and Node tests alike.
 */

const R_DENOM = 2 ** 52 // exact IEEE-754 double mantissa (Bustabit convention)

// Pinned drand quicknet constants (verified live 2026-07-23). NEVER fetched at
// verify-time (spec §3.2). public_key is the 96-byte BLS12-381 G2 group key.
export const DRAND_QUICKNET = {
  beaconId: 'quicknet',
  chainHash: '52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971',
  publicKey:
    '83cf0f2896adee7eb8b5f01fcad3912212c437e0073e911fb90022d3e760183c' +
    '8c4b450b6a0a6c3ac6a5776a2d1064510d1fec758c921cc22b0e17e63aaf4bcb' +
    '5ed66304de9cf809bd274ca73bab4af5a6e9c76a4bc09e76eae8991ef5ece45a',
  scheme: 'bls-unchained-g1-rfc9380',
  genesis: 1692803367,
  period: 3
} as const

const BEACON_RE = /^(?:[0-9a-f]{2})*$/ // empty OR even-length lowercase hex
const LABEL_RE = /^[a-z_]+$/

function subtle(): SubtleCrypto {
  const s = globalThis.crypto?.subtle
  if (!s) throw new Error('WebCrypto SubtleCrypto unavailable')
  return s
}

function toHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('')
}

function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) throw new Error(`odd-length hex: ${hex}`)
  const out = new Uint8Array(hex.length / 2)
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  return out
}

/** SHA-256 of the ASCII hex STRING (not the decoded bytes). Chain + commitment. */
export async function sha256Ascii(hexStr: string): Promise<string> {
  return toHex(await subtle().digest('SHA-256', new TextEncoder().encode(hexStr)))
}

/** Per-round commitment == the chain link == seed[j-1]. */
export async function serverSeedHash(serverSeed: string): Promise<string> {
  return sha256Ascii(serverSeed)
}

/** True iff sha256Ascii(serverSeed) === prevHash (the previous link / root). */
export async function verifyLink(serverSeed: string, prevHash: string): Promise<boolean> {
  return (await sha256Ascii(serverSeed)) === prevHash
}

/** Apply sha256Ascii chainIndex times -> the published root. */
export async function walkToRoot(serverSeed: string, chainIndex: number): Promise<string> {
  let h = serverSeed
  for (let i = 0; i < chainIndex; i++) h = await sha256Ascii(h)
  return h
}

function assertDomain(beacon: string, label: string, i: number): void {
  if (!BEACON_RE.test(beacon))
    throw new Error(`bad beacon (empty or lowercase hex pairs): ${beacon}`)
  if (!LABEL_RE.test(label)) throw new Error(`bad label (^[a-z_]+$): ${label}`)
  if (!Number.isInteger(i) || i < 0) throw new Error(`bad i (non-negative int): ${i}`)
}

/** HMAC-SHA256(key = decoded seed bytes, msg = utf-8 message) as hex. */
export async function hmacSha256Hex(serverSeed: string, message: string): Promise<string> {
  const key = await subtle().importKey(
    'raw',
    hexToBytes(serverSeed) as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )
  const sig = await subtle().sign('HMAC', key, new TextEncoder().encode(message) as BufferSource)
  return toHex(sig)
}

/**
 * 13-hex (52-bit) domain-separated draw. preimage = `${beacon}:${label}:${i}`
 * (empty beacon KEEPS its delimiter -> ':crash:0'). HMAC key = decoded seed bytes.
 */
export async function draw13(
  serverSeed: string,
  beacon: string,
  label: string,
  i: number
): Promise<string> {
  assertDomain(beacon, label, i)
  return (await hmacSha256Hex(serverSeed, `${beacon}:${label}:${i}`)).slice(0, 13)
}

/** Uniform float in [0, 1) from the 52-bit draw. */
export async function uniform(
  serverSeed: string,
  beacon: string,
  label: string,
  i: number
): Promise<number> {
  return parseInt(await draw13(serverSeed, beacon, label, i), 16) / R_DENOM
}

/** drand round pinned as the first round at/after betting-close. */
export function beaconRound(bettingCloseUnixS: number): number {
  return Math.floor((bettingCloseUnixS - DRAND_QUICKNET.genesis) / DRAND_QUICKNET.period) + 1
}
